import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const AuthContext = createContext(null);

const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours of inactivity
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => {
    try {
      const saved = localStorage.getItem('rh_admin');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      // Check if session already expired while tab was closed
      const lastActive = parseInt(localStorage.getItem('rh_last_active') || '0');
      if (Date.now() - lastActive > TIMEOUT_MS) {
        localStorage.removeItem('rh_admin');
        localStorage.removeItem('rh_last_active');
        return null;
      }
      return parsed;
    } catch { return null; }
  });

  const timerRef = useRef(null);

  const logout = useCallback(() => {
    setAdmin(null);
    localStorage.removeItem('rh_admin');
    localStorage.removeItem('rh_last_active');
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    localStorage.setItem('rh_last_active', String(Date.now()));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      logout();
      // Redirect to login — use location.replace so back button doesn't return to admin
      window.location.replace('/admin/login');
    }, TIMEOUT_MS);
  }, [logout]);

  const login = useCallback((adminData) => {
    setAdmin(adminData);
    localStorage.setItem('rh_admin', JSON.stringify(adminData));
    resetTimer();
  }, [resetTimer]);

  // Attach activity listeners while logged in
  useEffect(() => {
    if (!admin) return;

    // Start the timer immediately on mount
    resetTimer();

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [admin, resetTimer]);

  return (
    <AuthContext.Provider value={{ admin, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}